// A single horizontal bar split into parts (revamp PR 5): the manifest priority bar, any part-to-whole of a few
// states. The Lumen `Progress` holds one value; this holds several. Segments are split by a 2px gap, the bar has
// 4px rounded ends, and an unfilled rest of the track is `surface-2`. Recharts-free.
//
//   <StackedMeter
//     aria-label={t('…priorityBreakdown')}
//     segments={[
//       { key: 'critical', value: 5, tone: 'err', label: t('…critical') },
//       { key: 'medium', value: 12, tone: 'warn', label: t('…medium') },
//       { key: 'normal', value: 100, tone: 'neutral', label: t('…normal') },
//     ]}
//     total={150}            // optional: a larger total leaves the rest of the track empty
//     legend                 // a row of LegendSwatch with each count under the bar
//   />
//
// Each segment takes a status `tone` (state) or a series `slot` (identity), never both. The bar itself is
// `role="img"` with the aria-label; the legend carries the values as text, so nothing depends on colour alone.
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { LegendSwatch } from './legend-swatch';
import { SERIES_BG, STATUS_BG, type SeriesColorSlot, type StatusTone } from './series';
import { meterWidths } from './stacked-meter-logic';

export type StackedMeterSegment = {
  key: string;
  value: number;
  label: ReactNode;
} & ({ tone: StatusTone; slot?: never } | { slot: SeriesColorSlot; tone?: never });

export interface StackedMeterProps {
  segments: StackedMeterSegment[];
  /** Default: the sum of the segments. */
  total?: number;
  'aria-label': string;
  /** Show a legend row with each segment's label and value. */
  legend?: boolean;
  /** Formats a value in the legend (default: as is). */
  formatValue?: (value: number) => ReactNode;
  /** Bar height in px (default 8). */
  height?: number;
  className?: string;
}

const colourOf = (segment: StackedMeterSegment) =>
  segment.slot !== undefined ? SERIES_BG[segment.slot] : STATUS_BG[segment.tone as StatusTone];

export function StackedMeter({
  segments,
  total,
  'aria-label': ariaLabel,
  legend = false,
  formatValue,
  height = 8,
  className,
}: StackedMeterProps) {
  const widths = meterWidths(segments, total);
  const rest = 100 - widths.reduce((sum, segment) => sum + segment.percent, 0);
  return (
    <div data-slot="stacked-meter" className={cn('grid gap-2.5', className)}>
      <div
        role="img"
        aria-label={ariaLabel}
        className="flex w-full gap-0.5 overflow-hidden rounded-tag"
        style={{ height }}
      >
        {/* Shares grow from a 0 basis, so the 2px gaps (the card surface showing through) come out of the track. */}
        {widths.map((segment, index) => (
          <span
            key={segment.key}
            className={cn('h-full min-w-px', index === widths.length - 1 && 'rounded-r-tag', colourOf(segment))}
            style={{ flex: `${segment.percent} 1 0%` }}
          />
        ))}
        {rest > 0.01 && <span aria-hidden className="h-full bg-surface-2" style={{ flex: `${rest} 1 0%` }} />}
      </div>
      {legend && (
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {segments.map((segment) =>
            segment.slot !== undefined ? (
              <LegendSwatch
                key={segment.key}
                slot={segment.slot}
                label={segment.label}
                value={formatValue ? formatValue(segment.value) : segment.value}
              />
            ) : (
              <LegendSwatch
                key={segment.key}
                tone={segment.tone as StatusTone}
                label={segment.label}
                value={formatValue ? formatValue(segment.value) : segment.value}
              />
            )
          )}
        </div>
      )}
    </div>
  );
}
